/**
 * App.tsx
 * ------------------------------------------------------------------
 * Root component. Renders the warm-glow Backdrop once, wraps the app
 * in Auth + Settings providers, and routes:
 *   - authorised            -> Shell
 *   - unauthenticated        -> Landing (default) or Login
 *   - signed in, unauthorised -> access-denied notice
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { Landing } from '@/features/landing/Landing';
import { LandingIndex } from '@/features/landing/LandingIndex';

/*
 * The landing page is the editorial index (LandingIndex): one filtered list of
 * the work, near-monochrome so the only colour is the screenshots', and dark
 * mode following the reader. Chosen 2026-10-01 over two alternatives.
 *
 * The previous page and the two rejected directions stay reachable for
 * comparison and as a fallback, none of them linked from anywhere:
 *   /          the editorial index
 *   /?v=live   the previous page, unchanged
 *   /?v=a      products lead, client work condensed
 *   /?v=b      house of brands: a band per product
 */
export type LandingVariant = 'live' | 'a' | 'b' | 'c';

function landingVariant(): LandingVariant {
  if (typeof window === 'undefined') return 'c';
  const q = new URLSearchParams(window.location.search);
  if (q.has('next')) return 'a';
  const v = q.get('v');
  return v === 'live' || v === 'a' || v === 'b' ? v : 'c';
}
import { Login } from '@/features/auth/Login';
import { PublicLeadForm } from '@/features/leads/PublicLeadForm';
import { LegalPage, legalSlugFor } from '@/features/legal/LegalPage';
import { Shell } from '@/components/Shell';
import { Backdrop } from '@/components/ui/glass';
import { Button } from '@/components/ui/primitives';

type PublicView = 'landing' | 'login';

function Gate() {
  const { authorised, loading, user, signOut } = useAuth();
  const [view, setView] = useState<PublicView>('landing');

  // Public enquiry form (no auth) — reachable at ?enquire or #enquire.
  const enquire =
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('enquire') || window.location.hash.replace('#', '') === 'enquire');
  if (enquire) return <PublicLeadForm />;

  // Public legal pages (no auth) — /privacy, /terms, /data-deletion. Rendered
  // before the auth check, like the enquiry form: Meta's reviewers and anyone
  // following a link from a message are not signed in.
  const legal = typeof window !== 'undefined' ? legalSlugFor(window.location) : null;
  if (legal) return <LegalPage slug={legal} />;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-gray-400">Loading…</div>
      </div>
    );
  }

  // Signed in but not authorised (e.g. an email with no users record).
  if (user && !authorised) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="surface rounded-card max-w-sm p-8 text-center">
          <h1 className="text-lg font-semibold text-primary dark:text-gray-100">
            Access not enabled
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            The account <span className="font-medium">{user.email}</span> is not
            authorised for the Acepsle Hub. Ask an operator to add you, or sign
            in with an authorised account.
          </p>
          <div className="mt-5 flex justify-center">
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!authorised) {
    if (view !== 'landing') return <Login onBack={() => setView('landing')} />;
    return landingVariant() === 'c' ? (
      <LandingIndex onSignIn={() => setView('login')} />
    ) : (
      <Landing onSignIn={() => setView('login')} variant={landingVariant()} />
    );
  }

  return (
    <SettingsProvider>
      <Shell />
    </SettingsProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Backdrop />
      <Gate />
    </AuthProvider>
  );
}
